import { Asset, ReferenceDocument } from './asset';
import { inspectionTypes, inspectionTypesCombined, probabilities } from './common';

/**
 * The inspection plan entity.
 */
export type Plan = {
    id: number;
    blueprint_damages: string[];
    revision_history: { date?: string; author: string; comments: string; reviewed: string }[];
    program_implementation: typeof probabilities[number];
    internal_inspection_interval: number;
    external_inspection_interval: number;
    internal_due_date: Date;
    external_due_date: Date;
    draft_data: {};
    asset?: Asset; // should be ShortAsset
    inspection_plan_group?: InspectionPlanGroup;
    inspection_activity?: InspectionActivity[];
    damage_mechanism?: DamageMechanism[];
    thickness_requirement?: ThicknessRequirement;
    risk_assessment?: RiskAssessment;
    reference_documents?: ReferenceDocument[];
};

export type ShortPlan = Pick<Plan, 'id'>;

export type InspectionPlanGroup = {
    id: number;
    name: string;
};

export type InspectionActivity = {
    id: number;
    type: typeof inspectionTypes[number];
    required: boolean;
    comments: string;
    asset_state: string;
    preparation_steps: string[];
    methods: { [type: string]: string[] };
    last_performed?: Date;
    inspection_plan: number; // TODO change to { id: number } -> ShortPlan
};

export type DamageMechanism = {
    id: number;
    type: typeof inspectionTypesCombined[number];
    probability: typeof probabilities[number];
    description: string;
    inspection_plan?: number; // TODO change to { id: number } -> ShortPlan
    damage_descriptor?: DamageMechanismDescriptor;
};

export type DamageMechanismDescriptor = {
    code: number;
    name: string;
};

export type ThicknessRequirement = {
    id: number;
    description: string;
    thickness: { component: string; thickness: string; unit: string }[];
};

export type RiskAssessment = {
    id: number;
    likelihood_risk: typeof probabilities[number];
    likelihood_description: string;
    consequence_risk: typeof probabilities[number];
    consequence_description: string;
};

export const likelihoodIndex = Object.freeze({
    low: 4,
    'moderate-low': 3,
    moderate: 2,
    'moderate-high': 1,
    high: 0,
} as const);
export const consequenceIndex = Object.freeze({
    low: 0,
    'moderate-low': 1,
    moderate: 2,
    'moderate-high': 3,
    high: 4,
} as const);
export const overallRiskMatrix = Object.freeze([
    ['moderate-high', 'moderate-high', 'moderate-high', 'high', 'high'],
    ['moderate', 'moderate', 'moderate-high', 'moderate-high', 'high'],
    ['low', 'low', 'moderate', 'moderate-high', 'high'],
    ['low', 'low', 'moderate', 'moderate', 'moderate-high'],
    ['low', 'low', 'moderate', 'moderate', 'moderate-high'],
] as const);

export const computeOverallRisk = (risk: RiskAssessment) =>
    overallRiskMatrix?.[likelihoodIndex[risk.likelihood_risk]]?.[consequenceIndex[risk.consequence_risk]] ??
    'not-defined';

export const computeOverallRiskLikelihoodConsequence = (
    likelihood: typeof probabilities[number],
    consequence: typeof probabilities[number],
) => overallRiskMatrix?.[likelihoodIndex[likelihood]]?.[consequenceIndex[consequence]] ?? 'not-defined';

export const newPlan = (): Plan => ({
    id: 0,
    blueprint_damages: [],
    revision_history: [],
    program_implementation: 'high',
    internal_inspection_interval: 0,
    external_inspection_interval: 0,
    internal_due_date: new Date(),
    external_due_date: new Date(),
    draft_data: {},
});

export const newInspectionActivity = (): InspectionActivity => ({
    id: 0,
    type: 'internal',
    required: true,
    comments: '',
    asset_state: 'planned',
    preparation_steps: [],
    methods: {
        internal: [],
        external: [],
    },
    inspection_plan: 0,
});
